import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { FaFileExcel } from 'react-icons/fa';
import { ExcelSheetData } from '../utils/types';
import { parseExcelFile } from '../utils/excelParser';
import styles from './StepUpload.module.scss';

interface Props {
    excelData: ExcelSheetData | null;
    fileName: string;
    onFileLoaded: (data: ExcelSheetData, fileName: string) => void;
    onError: (message: string) => void;
}

const StepUpload: React.FC<Props> = ({ excelData, fileName, onFileLoaded, onError }) => {
    const { t } = useTranslation();

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        if (!acceptedFiles.length) return;

        const file = acceptedFiles[0];
        try {
            const data = await parseExcelFile(file);
            if (data.rows.length === 0) {
                onError(t('budgets.budget.import_structure.error_empty_file'));
                return;
            }
            onFileLoaded(data, file.name);
        } catch {
            onError(t('budgets.budget.import_structure.error_invalid_file'));
        }
    }, [onFileLoaded, onError, t]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
        },
        multiple: false,
    });

    return (
        <div className={styles.container}>
            <div className={styles.description}>
                <p>{t('budgets.budget.import_structure.step_upload_description_1')}</p>
            </div>

            <div {...getRootProps()} className={`${styles.dropzone} ${isDragActive ? styles.active : ''} ${excelData ? styles.hasFile : ''}`}>
                <input {...getInputProps()} />
                <FaFileExcel className={styles.icon} />
                {excelData
                    ? (
                        <div className={styles.fileInfo}>
                            <span className={styles.fileName}>{fileName}</span>
                            <span className={styles.rowCount}>
                                {t('budgets.budget.import_structure.rows_found', { count: excelData.rows.length })}
                            </span>
                        </div>
                    )
                    : (
                        <span>{t('budgets.budget.import_structure.drop_file_text')}</span>
                    )
                }
            </div>
        </div>
    );
};

export default StepUpload;
